// src/pages/rh/AlertesContrats.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Table, Row, Col, Form, Button, Pagination,
    Spinner, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   PAGINATION
   ============================================================ */
const TablePagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <Pagination className="pagination-charte mb-0">
            <Pagination.Prev onClick={() => onPageChange(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                    <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => onPageChange(pageNum)}>
                        {pageNum}
                    </Pagination.Item>
                );
            })}
            <Pagination.Next onClick={() => onPageChange(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
    );
};

/* ============================================================
   ALERT TABLE — Design charte
   ============================================================ */
const AlertTable = ({ title, icon, gradient, data, columns, onView }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        setCurrentPage(1);
    }, [data.length]);

    if (data.length === 0) return null;

    const totalPages = Math.ceil(data.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentData = data.slice(startIndex, startIndex + itemsPerPage);

    return (
        <Col lg={12}>
            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                <Card.Header className="card-alert-header" style={{ background: gradient }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${icon} text-white fs-5`}></i>
                        <span className="fw-semibold text-white">{title}</span>
                        <span className="badge-header-light ms-2">{data.length}</span>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0 table-charte">
                            <thead>
                                <tr>
                                    {columns.map((col, idx) => (
                                        <th key={idx} className={col.className || ''}>{col.label}</th>
                                    ))}
                                    <th className="text-center pe-4">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentData.map((item) => (
                                    <tr key={item.id}>
                                        {columns.map((col, colIdx) => (
                                            <td key={colIdx} className={col.className || ''}>
                                                {col.render ? col.render(item) : item[col.field]}
                                            </td>
                                        ))}
                                        <td className="text-center pe-4">
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                                <button
                                                    className="action-btn-charte"
                                                    onClick={() => onView(item.id)}
                                                    style={{ '--btn-color': COLORS.primary }}
                                                >
                                                    <i className="ri-eye-line"></i>
                                                </button>
                                            </OverlayTrigger>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    {totalPages > 1 && (
                        <div className="pagination-wrapper-charte">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, data.length)}</strong> sur <strong>{data.length}</strong>
                            </div>
                            <TablePagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function AlertesContrats() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [alerts, setAlerts] = useState({
        debut_proche: [],
        fin_proche: [],
        expires: [],
        encours: []
    });
    const [stats, setStats] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('');
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchAlerts();
        fetchStats();
    }, []);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchAlerts = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?action=alerts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement alertes");
            const result = await response.json();
            setAlerts(result.data);
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    /* ============================================================
       FILTRES
       ============================================================ */
    const filterData = (list) => {
        if (!filterText && !filterType) return list;
        return list.filter(item => {
            const matchText = !filterText ||
                (item.employe_nom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.employe_prenom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.employe_email || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.employeur_nom || '').toLowerCase().includes(filterText.toLowerCase());
            return matchText;
        });
    };

    const filtered = {
        debut_proche: filterData(alerts.debut_proche || []),
        fin_proche: filterData(alerts.fin_proche || []),
        expires: filterData(alerts.expires || []),
        encours: filterData(alerts.encours || [])
    };

    const shouldShow = (type) => !filterType || filterType === type;
    const resetFilters = () => { setFilterText(''); setFilterType(''); };

    /* ============================================================
       HELPERS
       ============================================================ */
    const renderEmployee = (item) => (
        <div className="d-flex align-items-center gap-3">
            <div className="employee-avatar-charte">
                {item.employe_prenom?.charAt(0)}{item.employe_nom?.charAt(0)}
            </div>
            <div>
                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                    {item.employe_prenom} {item.employe_nom}
                </div>
                <small className="text-muted">
                    <i className="ri-mail-line me-1" style={{ fontSize: '0.7rem' }}></i>
                    {item.employe_email || '—'}
                </small>
            </div>
        </div>
    );

    const renderEmployer = (item) => (
        <div>
            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{item.employeur_nom}</div>
            <small className="text-muted">{item.employeur_email || '—'}</small>
        </div>
    );

    /* ============================================================
       COLONNES
       ============================================================ */
    const debutProcheColumns = [
        { label: 'Employé', className: 'ps-4', render: renderEmployee },
        { label: 'Employeur', render: renderEmployer },
        {
            label: 'Date de début',
            render: (item) => (
                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                    <i className="ri-calendar-line"></i>
                    {formatDate(item.date_debut)}
                </span>
            )
        },
        {
            label: 'Statut',
            className: 'text-center',
            render: () => (
                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                    <i className="ri-time-line"></i>À venir
                </span>
            )
        }
    ];

    const finProcheColumns = [
        { label: 'Employé', className: 'ps-4', render: renderEmployee },
        { label: 'Employeur', render: renderEmployer },
        {
            label: 'Date de fin',
            render: (item) => (
                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <i className="ri-calendar-close-line"></i>
                    {formatDate(item.date_fin)}
                </span>
            )
        },
        {
            label: 'Jours restants',
            className: 'text-center',
            render: (item) => {
                const dateFin = new Date(item.date_fin);
                const today = new Date();
                const joursRestants = Math.ceil((dateFin - today) / (1000 * 60 * 60 * 24));
                return (
                    <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                        <i className="ri-time-line"></i>{joursRestants} jour(s)
                    </span>
                );
            }
        }
    ];

    const expiresColumns = [
        { label: 'Employé', className: 'ps-4', render: renderEmployee },
        { label: 'Employeur', render: renderEmployer },
        {
            label: "Date d'expiration",
            render: (item) => (
                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <i className="ri-calendar-close-line"></i>
                    {formatDate(item.date_fin)}
                </span>
            )
        },
        {
            label: 'Statut',
            className: 'text-center',
            render: () => (
                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <i className="ri-error-warning-line"></i>Expiré
                </span>
            )
        }
    ];

    const encoursColumns = [
        { label: 'Employé', className: 'ps-4', render: renderEmployee },
        { label: 'Employeur', render: renderEmployer },
        { label: 'Date de début', render: (item) => <span className="text-muted">{formatDate(item.date_debut)}</span> },
        { label: 'Date de fin', render: (item) => <span className="text-muted">{item.date_fin ? formatDate(item.date_fin) : 'CDI'}</span> },
        {
            label: 'Statut',
            className: 'text-center',
            render: () => (
                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                    <i className="ri-checkbox-circle-line"></i>Actif
                </span>
            )
        }
    ];

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-alert-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des alertes
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const totalAlerts = filtered.debut_proche.length + filtered.fin_proche.length + filtered.expires.length + filtered.encours.length;

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                                <i className="ri-alert-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Alertes contrats</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {stats?.total || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-checkbox-circle-line"></i> Actifs {stats?.actifs || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-file-text-line"></i> CDI {stats?.cdi || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-error-warning-line"></i> Expirés {stats?.expires || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchAlerts} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/rh/contrats')}>
                                <i className="ri-arrow-left-line"></i> Retour aux contrats
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-file-copy-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Total contrats</span>
                                        <h3 className="kpi-value-charte">{stats.total || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-checkbox-circle-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Contrats actifs</span>
                                        <h3 className="kpi-value-charte">{stats.actifs || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-info">
                                    <div className="kpi-icon-charte"><i className="ri-file-text-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">CDI</span>
                                        <h3 className="kpi-value-charte">{stats.cdi || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-danger">
                                    <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Expirés</span>
                                        <h3 className="kpi-value-charte">{stats.expires || 0}</h3>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par employé, employeur..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="">Tous les types</option>
                                        <option value="debut">Début proche</option>
                                        <option value="fin">Fin proche</option>
                                        <option value="expire">Expiré</option>
                                        <option value="encours">En cours</option>
                                    </Form.Select>
                                </Col>
                                <Col md="3">
                                    <div className="d-flex gap-2">
                                        {(filterText || filterType) && (
                                            <Button onClick={resetFilters} className="btn-modern-light-outline">
                                                <i className="ri-close-line"></i> Effacer
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                                <Col md="2" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {totalAlerts} alerte{totalAlerts > 1 ? 's' : ''}
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAUX D'ALERTES ===== */}
                    <Row className="g-4">
                        {shouldShow('debut') && (
                            <AlertTable
                                title="Contrats qui commencent bientôt"
                                icon="ri-calendar-line"
                                gradient={`linear-gradient(135deg, ${COLORS.info}, #0dcaf0)`}
                                data={filtered.debut_proche}
                                columns={debutProcheColumns}
                                onView={(id) => navigate(`/rh/contrats/${id}`)}
                            />
                        )}
                        {shouldShow('fin') && (
                            <AlertTable
                                title="Contrats qui expirent bientôt"
                                icon="ri-alert-line"
                                gradient={`linear-gradient(135deg, ${COLORS.warning}, #d97706)`}
                                data={filtered.fin_proche}
                                columns={finProcheColumns}
                                onView={(id) => navigate(`/rh/contrats/${id}`)}
                            />
                        )}
                        {shouldShow('expire') && (
                            <AlertTable
                                title="Contrats expirés"
                                icon="ri-error-warning-line"
                                gradient={`linear-gradient(135deg, ${COLORS.danger}, #dc2626)`}
                                data={filtered.expires}
                                columns={expiresColumns}
                                onView={(id) => navigate(`/rh/contrats/${id}`)}
                            />
                        )}
                        {shouldShow('encours') && (
                            <AlertTable
                                title="Contrats en cours"
                                icon="ri-checkbox-circle-line"
                                gradient={`linear-gradient(135deg, ${COLORS.success}, #059669)`}
                                data={filtered.encours}
                                columns={encoursColumns}
                                onView={(id) => navigate(`/rh/contrats/${id}`)}
                            />
                        )}

                        {totalAlerts === 0 && (
                            <Col lg={12}>
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Body className="py-5">
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}15, ${COLORS.success}05)`, color: COLORS.success }}>
                                                <i className="ri-checkbox-circle-line"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-1">Aucune alerte</h6>
                                            <p className="text-muted small mb-0">Tous les contrats sont à jour</p>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--warning);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-modern-light-outline {
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white;
                    border-radius: 50px;
                    padding: 0.5rem 1rem;
                    font-size: 0.85rem;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-light-outline:hover {
                    background: rgba(255,255,255,0.25);
                    color: white;
                }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER ALERT ===== */
                .card-alert-header {
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }

                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .employee-avatar-charte {
                    width: 42px; height: 42px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    color: var(--primary);
                    display: flex; align-items: center; justify-content: center;
                    font-weight: 700; font-size: 0.85rem;
                    flex-shrink: 0;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--warning);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--warning);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}